import AddIcon from "@mui/icons-material/Add";
import EmailIcon from "@mui/icons-material/Email";
import GetAppIcon from "@mui/icons-material/GetApp";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { SlidingCubeLoader } from "react-loaders-kit";
import { useNavigate } from "react-router-dom";
import Toast from "../../../../Components/Toast";
import useAuthStore from "../../../../store/useAuthStore";
import useCompanyStore from "../../../../store/useCompanyStore";
import Divider from "@mui/material/Divider";
import Typography from '@mui/material/Typography';
// Import jsPDF and its AutoTable plugin for PDF generation
import jsPDF from "jspdf";
import "jspdf-autotable";

const pastelColors = [
  "#f07167", "#335c67", "#7f5539", "#f28482", "#f5cac3",
  "#6b705c", "#cb997e", "#9d6b53", "#c9cba3", "#eae0d5",
  "#b392ac", "#735d78", "#e27396", "#f29559", "#acd8aa"
];

const getRandomColor = (() => {
  const colorMap = new Map();
  return (id) => {
    if (!colorMap.has(id)) {
      colorMap.set(id, pastelColors[Math.floor(Math.random() * pastelColors.length)]);
    }
    return colorMap.get(id);
  };
})();

const getInitials = (name) =>
  name ? name.split(" ").map((n) => n[0]).join("") : "";

const actionOptions = [
  "Generate (Single)",
  "Bulk Updates"
];

const formatCurrency = (amount) => Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const PayslipTable = () => {
  const { token } = useAuthStore();
  const { companyInfo } = useCompanyStore();
  const companyName = companyInfo?.name || "Company";
  const navigate = useNavigate();

  const [logoBase64, setLogoBase64] = useState(null);

  // Pre-load company logo as base64 for PDF embedding
  useEffect(() => {
    if (!companyInfo?.logo) return;
    fetch(companyInfo.logo)
      .then((r) => r.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, [companyInfo?.logo]);

  // Main States
  const [payslips, setPayslips] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const dateStr = new Date();
  const prevMonthDate = new Date(dateStr.getFullYear(), dateStr.getMonth() - 1, 1);
  const pYear = prevMonthDate.getFullYear();
  const pMonth = (prevMonthDate.getMonth() + 1).toString().padStart(2, '0');
  const pLastDay = new Date(pYear, pMonth, 0).getDate();
  const pEndDay = pLastDay >= 30 ? 30 : pLastDay;

  const initialStartDate = `${pYear}-${pMonth}-01`;
  const initialEndDate = `${pYear}-${pMonth}-${pEndDay.toString().padStart(2, '0')}`;
  
  const [dates, setDates] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate
  });
  const [selectedAction, setSelectedAction] = useState("Generate (Single)");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // For download menu (PDF & CSV)
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  // New state for Email Dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Holds the _id's of payslips selected for email
  const [selectedPayslipIds, setSelectedPayslipIds] = useState([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("Pending");
  // New state for filtering by date
  const [filterDate, setFilterDate] = useState("");
  const [bulkMonth, setBulkMonth] = useState("");
  // New states for deletion
  const [selectedRows, setSelectedRows] = useState([]);
  // Add date filter state for email dialog
  const [emailDateFilter, setEmailDateFilter] = useState("");
  // Add these new states at the top with other states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(""); // "single" or "bulk"
  const [deleteTargetIds, setDeleteTargetIds] = useState([]); // array of ids to delete

  // -----------------------------
  // Fetch Functions
  // -----------------------------
  const fetchPayslips = async () => {
    try {
      const response = await fetch("/api/payslips", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPayslips(data.payslips);
    } catch (error) {
      console.error("Error fetching payslips:", error);
    }
  };

  // Fetch payslips only for the selected period
  const fetchPayslipsForPeriod = async (start, end) => {
    try {
      const response = await fetch("/api/payslips", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      const periodPayslips = data.payslips.filter((p) => {
        const pStart = new Date(p.startDate).toISOString().split("T")[0];
        const pEnd = new Date(p.endDate).toISOString().split("T")[0];
        return pStart === start && pEnd === end;
      });
      setPayslips(periodPayslips);
    } catch (error) {
      console.error("Error fetching period payslips:", error);
    }
  };

  // Fetch employees (for single payslip generation)
  const fetchEmployees = async () => {
    try {
      const response = await fetch("/api/getallEmployees", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        const formattedEmployees = data.employees.map(emp => ({
          id: emp._id,
          name: emp.name,
          email: emp.email,
          phone: emp.phone,
          role: emp.role,
          dob: emp.dob,
          cid: emp.cid,
          department: emp.department,
          salary: emp.salary,
        }));
        setEmployees(formattedEmployees);
      }

    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchPayslips(), fetchEmployees()]);
      setIsLoading(false);
    };
    loadData();
  }, [token]); // We can ignore the warning for these dependencies

  // -----------------------------
  // Date Change Handler
  // -----------------------------
  const handleStartDateChange = (e) => {
    const monthVal = e.target.value; // "YYYY-MM"
    if (!monthVal) return;
    const [year, month] = monthVal.split("-");
    const startDate = `${year}-${month}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDay = lastDay >= 30 ? 30 : lastDay;
    const endDate = `${year}-${month}-${endDay.toString().padStart(2, "0")}`;
    setDates({ startDate, endDate });
  };

  // -----------------------------
  // Generate Payslip Handler
  // -----------------------------
  const handleGenerate = async () => {
    const start = new Date(dates.startDate);
    const end = new Date(dates.endDate);
    // Ensure the range is within the same month
    if (start.getMonth() !== end.getMonth() || start.getFullYear() !== end.getFullYear()) {
      setToastMessage("Please select a date range within the same month.");
      setShowToast(true);
      return;
    }

    // Check if payslips already exist for this period
    try {
      const response = await fetch("/api/payslips", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      const periodPayslips = data.payslips.filter((p) => {
        const pStart = new Date(p.startDate).toISOString().split("T")[0];
        const pEnd = new Date(p.endDate).toISOString().split("T")[0];
        return pStart === dates.startDate && pEnd === dates.endDate;
      });
      if (periodPayslips.length > 0) {
        setPayslips(periodPayslips);
        setToastMessage("Payslips for this period already exist.");
        setShowToast(true);
        setOpenDialog(false);
        return;
      }
    } catch (error) {
      console.error("Error checking existing payslips:", error);
    }

    // If not, generate the payslips
    setIsLoading(true);
    try {
      let url, body;
      if (selectedAction === "Generate (Single)") {
        if (!selectedEmployee) {
          setToastMessage("Please select an employee for single generation.");
          setShowToast(true);
          setIsLoading(false);
          return;
        }
        url = "/api/generatePayslip";
        body = { ...dates, employeeId: selectedEmployee, bankDetails: {} };
      } else if (selectedAction === "Bulk Updates") {
        url = "/api/generate-all-payslips";
        body = dates;
      }
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        await fetchPayslipsForPeriod(dates.startDate, dates.endDate);
        setOpenDialog(false);
        const data = await response.json();
        setToastMessage(data.message || "Payslips generated successfully");
        setShowToast(true);
      } else {
        const errData = await response.json();
        setToastMessage(errData.error || "Generation failed");
        setShowToast(true);
      }
    } catch (error) {
      console.error("Error generating payslips:", error);
    }
    setIsLoading(false);
  };

  // -----------------------------
  // PDF Generation Function (Modern Professional Template)
  // -----------------------------
  const generatePDFForPayslip = (payslip) => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageW = 595;
    const fmt = (amt) => `Nu. ${formatCurrency(amt)}`;

    const basicSalary = Number(payslip.basicSalary) || 0;
    const allowances = payslip.allowances || [];
    const deductions = payslip.deductions || [];
    const allowancesTotal = allowances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const grossSalary = basicSalary + allowancesTotal;
    const totalDeductions = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const netPay = grossSalary - totalDeductions;
    const taxableSalary = payslip.taxableSalary || Math.round(grossSalary * 0.85);
    const tds = payslip.tds ?? 0;

    // ── Header ──
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageW, 78, "F");
    doc.setFillColor(147, 197, 253);
    doc.rect(0, 78, pageW, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    if (logoBase64) {
      doc.addImage(logoBase64, "JPEG", 14, 10, 55, 55);
      doc.text(companyName.toUpperCase(), 78, 36);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("SALARY SLIP", 78, 56);
    } else {
      doc.text(companyName.toUpperCase(), 40, 36);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("SALARY SLIP", 40, 56);
    }

    const monthYear = new Date(payslip.startDate).toLocaleString("default", { month: "long", year: "numeric" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(monthYear.toUpperCase(), pageW - 40, 36, { align: "right" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Period: ${new Date(payslip.startDate).toLocaleDateString()} – ${new Date(payslip.endDate).toLocaleDateString()}`,
      pageW - 40, 56, { align: "right" }
    );

    // ── Employee Info Bar ──
    doc.setFillColor(239, 246, 255);
    doc.rect(0, 82, pageW, 72, "F");

    const empName = payslip.name || "N/A";
    const empId   = payslip.empId || (typeof payslip.employeeId === "object" ? payslip.employeeId?.employeeId : null) || "N/A";
    const dept    = payslip.department || (typeof payslip.employeeId === "object" ? payslip.employeeId?.department : null) || "N/A";
    const bank    = payslip.bankDetails?.accountNumber || payslip.bankDetails?.account || "N/A";

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("EMPLOYEE INFORMATION", 40, 99);

    const labelX = 40, valX = 130, labelX2 = 320, valX2 = 410;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);

    [
      ["Name", empName, "Department", dept, 115],
      ["Employee ID", empId, "Bank / Cheque", bank, 133],
    ].forEach(([l1, v1, l2, v2, y]) => {
      doc.setFont("helvetica", "bold"); doc.text(l1 + ":", labelX, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v1), valX, y);
      doc.setFont("helvetica", "bold"); doc.text(l2 + ":", labelX2, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v2), valX2, y);
    });

    // ── Earnings & Deductions Combined Table ──
    const earningsRows = [
      ["Basic Salary", fmt(basicSalary)],
      ...allowances.map((a) => [a.name, fmt(a.amount)]),
    ];
    const deductionRows = deductions.map((d) => [d.name === "TDS" ? "TDS (Income Tax)" : d.name, fmt(d.amount)]);

    const maxLen = Math.max(earningsRows.length, deductionRows.length);
    while (earningsRows.length < maxLen) earningsRows.push(["", ""]);
    while (deductionRows.length < maxLen) deductionRows.push(["", ""]);

    const tableBody = earningsRows.map((e, i) => [e[0], e[1], deductionRows[i][0], deductionRows[i][1]]);
    tableBody.push(["Gross Salary", fmt(grossSalary), "Total Deductions", fmt(totalDeductions)]);

    doc.autoTable({
      startY: 170,
      head: [["EARNINGS", "AMOUNT", "DEDUCTIONS", "AMOUNT"]],
      body: tableBody,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [96, 165, 250], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 9 },
      columnStyles: {
        0: { halign: "left", cellWidth: 148 },
        1: { halign: "right", cellWidth: 110, textColor: [37, 99, 235] },
        2: { halign: "left", cellWidth: 148 },
        3: { halign: "right", cellWidth: 110, textColor: [220, 38, 38] },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = data.column.index < 2 ? [220, 252, 231] : [255, 237, 237];
          data.cell.styles.textColor = data.column.index < 2 ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.lineColor = [200, 210, 220];
        }
      },
      margin: { left: 27, right: 27 },
    });

    // ── Tax Summary ──
    const taxY = doc.lastAutoTable.finalY + 12;
    doc.autoTable({
      startY: taxY,
      head: [["Taxable Salary (85%)", "TDS / Income Tax"]],
      body: [[fmt(taxableSalary), fmt(tds)]],
      theme: "grid",
      styles: { fontSize: 9, halign: "center", cellPadding: 5, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [148, 163, 184], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 270 }, 1: { cellWidth: 270 } },
      margin: { left: 27, right: 27 },
    });

    // ── Net Pay Box ──
    const netY = doc.lastAutoTable.finalY + 14;
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(27, netY, pageW - 54, 46, 4, 4, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("NET PAY", 50, netY + 19);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Employee Net Pay", 50, netY + 33);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(fmt(netPay), pageW - 50, netY + 30, { align: "right" });

    // ── Footer ──
    doc.setDrawColor(226, 232, 240);
    doc.line(27, 795, pageW - 27, 795);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 163, 184);
    doc.text("This is a computer-generated document. No signature is required.", 27, 808);
    doc.text(`Generated by ${companyName} | ${new Date().toLocaleDateString()}`, pageW - 27, 808, { align: "right" });

    return doc;
  };

  // -----------------------------
  // Email Dialog Handlers
  // -----------------------------
  // Open the email dialog and pre-select all filtered payslip IDs.
  const handleOpenEmailDialog = () => {
    const allIds = getFilteredPayslips().map((p) => p._id);
    setSelectedPayslipIds(allIds);
    setEmailDialogOpen(true);
  };

  // Toggle selection for a given payslip ID.
  const togglePayslipSelection = (id) => {
    setSelectedPayslipIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  // Send emails for selected payslips.
  const handleSendEmails = async () => {
    if (selectedPayslipIds.length === 0) {
      setToastMessage("Please select at least one email to send.");
      setShowToast(true);
      return;
    }
    setIsLoading(true);
    try {
      for (let id of selectedPayslipIds) {
        const payslip = payslips.find((p) => p._id === id);
        if (!payslip) continue;
        // Skip payslips without an email address
        if (!payslip.email) {
          console.warn("No email found for payslip ID:", id);
          continue;
        }
        const pdfDoc = generatePDFForPayslip(payslip);
        // Get the PDF as a Data URI string
        const pdfDataURI = pdfDoc.output("datauristring");
        // Send email to backend endpoint
        const response = await fetch(
          `/api/send-payslip-email/${payslip._id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ email: payslip.email, pdf: pdfDataURI })
          }
        );
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Failed to send email");
        }
      }
      setToastMessage("Emails sent successfully.");
      setShowToast(true);
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Error sending emails:", error);
      setToastMessage("Error sending emails: " + error.message);
      setShowToast(true);
    }
    setIsLoading(false);
  };

  // -----------------------------
  // Bulk Status Update Handlers
  // -----------------------------
  const handleOpenBulkStatusDialog = () => {
    setBulkSelectedIds(getFilteredPayslips().map(p => p._id));
    setBulkStatus("Pending");
    setBulkStatusDialogOpen(true);
  };

  const toggleBulkPayslipSelection = (id) => {
    setBulkSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkMonth) {
      setToastMessage("Please select a month");
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/update-status-bulk", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ids: bulkSelectedIds,
          status: bulkStatus
        })
      });

      const respData = await response.json();
      console.log("Bulk status update response:", respData);

      if (response.ok) {
        // Always fetch all payslips after update
        const refreshed = await fetch("/api/payslips", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const refreshedData = await refreshed.json();
        const allPayslips = refreshedData.payslips || [];
        const [year, month] = bulkMonth.split("-");
        // Filter payslips for this month and status Confirmed
        const payslipsToConfirm = allPayslips.filter(p => {
          const end = new Date(p.endDate);
          const pYear = end.getFullYear();
          const pMonth = (end.getMonth() + 1).toString().padStart(2, '0');
          return pYear === parseInt(year) && pMonth === month && p.status === "Confirmed";
        });
        if (bulkStatus === "Confirmed") {
          console.log("[Bulk Expense Trigger] payslipsToConfirm:", payslipsToConfirm);
          if (payslipsToConfirm.length === 0) {
            console.log("[Bulk Expense Trigger] No confirmed payslips found for this month.");
          } else {
            await addOrUpdatePayrollExpense({ payslipsToConfirm });
            console.log("[Bulk Expense Trigger] addOrUpdatePayrollExpense called.");
          }
        }
        setPayslips(allPayslips);
        setToastMessage("Bulk status updated successfully");
        setShowToast(true);
        setBulkStatusDialogOpen(false);
      } else {
        setToastMessage(respData.message || "Bulk status update failed");
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage("Error updating bulk status");
      setShowToast(true);
    }
    setIsLoading(false);
  };

  // -----------------------------
  // Download Functions (PDF & CSV)
  // -----------------------------
  // Download Functions (PDF & CSV)
  // -----------------------------
  const downloadPDF = () => {
    const doc = new jsPDF("l"); // Using landscape for extended columns
    
    const uniqueAllowances = Array.from(new Set(
      getFilteredPayslips().flatMap(p => (Array.isArray(p.allowances) ? p.allowances : []).map(a => a.name))
    )).sort();

    const uniqueDeductions = Array.from(new Set(
      getFilteredPayslips().flatMap(p => (Array.isArray(p.deductions) ? p.deductions : []).filter(d => d.name !== "TDS").map(d => d.name))
    )).sort();

    const tableColumn = [
      "Employee", "Period", "Basic Salary",
      ...uniqueAllowances, "Gross Salary",
      ...uniqueDeductions, "Total Deductions",
      "Taxable Salary", "TDS", "Net Pay", "Status"
    ];

    const tableRows = [];

    getFilteredPayslips().forEach(payslip => {
      const basicSalary = Number(payslip.basicSalary) || 0;
      const allowances = Array.isArray(payslip.allowances) ? payslip.allowances : [];
      const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
      const grossSalary = basicSalary + totalAllowances;
      
      const deductions = Array.isArray(payslip.deductions) ? payslip.deductions : [];
      const totalDeductions = deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
      
      const netSalary = grossSalary - totalDeductions;
      const taxableSalary = payslip.taxableSalary ? Math.round(payslip.taxableSalary) : Math.round(grossSalary * 0.85);
      const tds = payslip.tds ?? 0;

      const allowanceVals = uniqueAllowances.map(name => {
        const a = allowances.find(x => x.name === name);
        return a ? formatCurrency(a.amount) : "0";
      });

      const deductionVals = uniqueDeductions.map(name => {
        const d = deductions.find(x => x.name === name);
        return d ? formatCurrency(d.amount) : "0";
      });

      const rowData = [
        payslip.name,
        `${new Date(payslip.startDate).toLocaleDateString()} - ${new Date(payslip.endDate).toLocaleDateString()}`,
        formatCurrency(basicSalary),
        ...allowanceVals,
        formatCurrency(grossSalary),
        ...deductionVals,
        formatCurrency(totalDeductions),
        formatCurrency(taxableSalary),
        formatCurrency(tds),
        formatCurrency(netSalary),
        payslip.status
      ];
      tableRows.push(rowData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
    });

    const filename = filterDate ? `payslips_${filterDate}.pdf` : 'payslips.pdf';
    doc.save(filename);
  };

  const downloadCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    const uniqueAllowances = Array.from(new Set(
      getFilteredPayslips().flatMap(p => (Array.isArray(p.allowances) ? p.allowances : []).map(a => a.name))
    )).sort();

    const uniqueDeductions = Array.from(new Set(
      getFilteredPayslips().flatMap(p => (Array.isArray(p.deductions) ? p.deductions : []).filter(d => d.name !== "TDS").map(d => d.name))
    )).sort();

    const headers = [
      "Employee", "Period", "Basic Salary",
      ...uniqueAllowances, "Gross Salary",
      ...uniqueDeductions, "Total Deductions",
      "Taxable Salary", "TDS", "Net Pay", "Status"
    ];

    csvContent += headers.map(h => `"${h}"`).join(",") + "\r\n";

    getFilteredPayslips().forEach(payslip => {
      const basicSalary = Number(payslip.basicSalary) || 0;
      const allowances = Array.isArray(payslip.allowances) ? payslip.allowances : [];
      const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
      const grossSalary = basicSalary + totalAllowances;
      
      const deductions = Array.isArray(payslip.deductions) ? payslip.deductions : [];
      const totalDeductions = deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
      
      const netSalary = grossSalary - totalDeductions;
      const taxableSalary = payslip.taxableSalary ? Math.round(payslip.taxableSalary) : Math.round(grossSalary * 0.85);
      const tds = payslip.tds ?? 0;

      const allowanceVals = uniqueAllowances.map(name => {
        const a = allowances.find(x => x.name === name);
        return a ? formatCurrency(a.amount) : "0";
      });

      const deductionVals = uniqueDeductions.map(name => {
        const d = deductions.find(x => x.name === name);
        return d ? formatCurrency(d.amount) : "0";
      });

      const row = [
        `"${payslip.name}"`,
        `"${new Date(payslip.startDate).toLocaleDateString()} - ${new Date(payslip.endDate).toLocaleDateString()}"`,
        `"${formatCurrency(basicSalary)}"`,
        ...allowanceVals.map(v => `"${v}"`),
        `"${formatCurrency(grossSalary)}"`,
        ...deductionVals.map(v => `"${v}"`),
        `"${formatCurrency(totalDeductions)}"`,
        `"${formatCurrency(taxableSalary)}"`,
        `"${formatCurrency(tds)}"`,
        `"${formatCurrency(netSalary)}"`,
        `"${payslip.status}"`
      ];
      csvContent += row.join(",") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const filename = filterDate ? `payslips_${filterDate}.csv` : 'payslips.csv';
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // -----------------------------
  // Update Status and Delete Handlers
  // -----------------------------


  // Add/Update Payroll Expense
  const addOrUpdatePayrollExpense = async ({ payslipsToConfirm }) => {
    if (!payslipsToConfirm.length) return;

    // Format date as YYYY-MM-DD (no timezone)
    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return d.toISOString().split('T')[0];
    };

    // Get month-year for description
    const getMonthYearStr = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    if (payslipsToConfirm.length === 1) {
      // Single confirmation
      const p = payslipsToConfirm[0];
      const grossSalary = (Number(p.basicSalary) || 0) + ((Array.isArray(p.allowances) ? p.allowances : []).reduce((s, a) => s + (Number(a.amount) || 0), 0));
      const header = "Direct Expense";
      const subheader = "Payroll Expense";
      const date = formatDate(p.endDate);
      const monthYear = getMonthYearStr(p.endDate);
      const description = `Payroll for ${monthYear} confirmed for: ${p.name}`;
      // Log before saving
      console.log("[Expense] Adding single payroll expense", { header, subheader, amount: grossSalary, description, date });
      await fetch("/api/addExpense", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ header, subheader, amount: grossSalary, description, date })
      });
    }
    else {
      // Bulk confirmation
      // Only sum gross salary of payslips with status Confirmed
      const confirmedPayslips = payslipsToConfirm.filter(p => p.status === "Confirmed");
      if (!confirmedPayslips.length) return;
      const totalGross = confirmedPayslips.reduce((sum, p) => {
        const basic = Number(p.basicSalary) || 0;
        const allowances = Array.isArray(p.allowances) ? p.allowances : [];
        const totalAllowances = allowances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
        return sum + basic + totalAllowances;
      }, 0);
      const header = "Direct Expense";
      const subheader = "Payroll Expense";
      const date = formatDate(confirmedPayslips[0].endDate);
      const monthYear = getMonthYearStr(confirmedPayslips[0].endDate);
      const confirmedNames = confirmedPayslips.map(p => p.name).join(", ");
      const description = `Payroll for ${monthYear} confirmed for: ${confirmedNames}`;
      // Log before saving
      console.log("[Expense] Adding bulk payroll expense", { header, subheader, amount: totalGross, description, date, count: confirmedPayslips.length });
      await fetch("/api/addExpense", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ header, subheader, amount: totalGross, description, date })
      });
    }
  };

  // Single status change
  const handleStatusChange = async (id, newStatus) => {
    const payslip = payslips.find(p => p._id === id);
    if (!payslip) return;

    // Prevent changing back to Pending
    if (
      (payslip.status === "Confirmed" || payslip.status === "Paid") &&
      newStatus === "Pending"
    ) {
      setToastMessage("Cannot change status back to Pending after confirmation.");
      setShowToast(true);
      return;
    }

    try {
      const response = await fetch(`/api/update-status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        // Use the updated payslip object for expense logic
        const updatedPayslip = { ...payslip, status: newStatus };
        setPayslips(
          payslips.map((p) => (p._id === id ? updatedPayslip : p))
        );
        setToastMessage("Status updated successfully");
        setShowToast(true);

        // If status is confirmed, add/update expense
        if (newStatus === "Confirmed") {
          await addOrUpdatePayrollExpense({ payslipsToConfirm: [updatedPayslip] });
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  // Handle single delete icon click
  const handleSingleDeleteClick = (id, e) => {
    e.stopPropagation();
    setDeleteMode("single");
    setDeleteTargetIds([id]);
    setDeleteDialogOpen(true);
  };

  // Handle bulk delete button click
  const handleBulkDeleteClick = () => {
    if (!selectedRows.length) {
      setToastMessage("Please select payslips to delete");
      setShowToast(true);
      return;
    }
    setDeleteMode("bulk");
    setDeleteTargetIds([...selectedRows]);
    setDeleteDialogOpen(true);
  };

  // Confirm deletion (single or bulk)
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      if (deleteMode === "single") {
        // Single delete API
        const response = await fetch(`/api/deletePayslip/${deleteTargetIds[0]}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          setToastMessage("Payslip deleted successfully");
        } else {
          setToastMessage("Failed to delete payslip");
        }
      } else if (deleteMode === "bulk") {
        // Bulk delete API
        const response = await fetch("/api/delete-payslips-bulk", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ids: deleteTargetIds })
        });
        if (response.ok) {
          setToastMessage("Selected payslips deleted successfully");
        } else {
          setToastMessage("Failed to delete payslips");
        }
      }
      await fetchPayslips();
      setShowToast(true);
      setSelectedRows([]); // Clear DataGrid selection after bulk delete
    } catch (error) {
      setToastMessage("Error deleting payslip(s)");
      setShowToast(true);
    }
    setIsLoading(false);
    setDeleteDialogOpen(false);
    setDeleteTargetIds([]);
    setDeleteMode("");
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetIds([]);
    setDeleteMode("");
  };

  // -----------------------------
  // DataGrid Columns Definition
  // -----------------------------
  const uniqueAllowances = Array.from(new Set(
    payslips.flatMap(p => (Array.isArray(p.allowances) ? p.allowances : []).map(a => a.name))
  )).sort();

  const uniqueDeductions = Array.from(new Set(
    payslips.flatMap(p => (Array.isArray(p.deductions) ? p.deductions : []).filter(d => d.name !== "TDS").map(d => d.name))
  )).sort();

  const allowanceColumns = uniqueAllowances.map(name => ({
    field: `allowance_${name}`,
    headerName: `+ ${name}`,
    width: 140,
    renderCell: (params) => {
      const allowances = Array.isArray(params?.row?.allowances) ? params.row.allowances : [];
      const allowance = allowances.find(a => a.name === name);
      return allowance ? formatCurrency(allowance.amount) : "0";
    }
  }));

  const deductionColumns = uniqueDeductions.map(name => ({
    field: `deduction_${name}`,
    headerName: `- ${name}`,
    width: 140,
    renderCell: (params) => {
      const deductions = Array.isArray(params?.row?.deductions) ? params.row.deductions : [];
      const deduction = deductions.find(d => d.name === name);
      return deduction ? formatCurrency(deduction.amount) : "0";
    }
  }));

  const columns = [
    {
      field: "name",
      headerName: "Employee",
      width: 200,
      renderCell: (params) => (
        <div className="flex items-center gap-3">
          <Avatar sx={{ bgcolor: getRandomColor(params?.row?.employeeId?.id || params?.row?.id) }}>
            {getInitials(params?.row?.name || "")}
          </Avatar>
          <span>{params?.row?.name || ""}</span>
        </div>
      )
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 120,
      renderCell: (params) =>
        params?.row?.startDate ? new Date(params.row.startDate).toLocaleDateString() : ""
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 120,
      renderCell: (params) =>
        params?.row?.endDate ? new Date(params.row.endDate).toLocaleDateString() : ""
    },
    {
      field: "basicSalary",
      headerName: "Basic Salary",
      width: 130,
      renderCell: (params) => formatCurrency(params?.row?.basicSalary)
    },
    ...allowanceColumns,
    {
      field: "grossSalary",
      headerName: "Gross Salary",
      width: 140,
      renderCell: (params) => {
        if (params?.row?.basicSalary == null) return "";
        const basicSalary = Number(params.row.basicSalary) || 0;
        const allowances = Array.isArray(params.row.allowances) ? params.row.allowances : [];
        const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
        return formatCurrency(basicSalary + totalAllowances);
      }
    },
    ...deductionColumns,
    {
      field: "deductions",
      headerName: "Total Deductions",
      width: 140,
      renderCell: (params) => {
        if (!params?.row?.deductions) return "";
        const deductions = Array.isArray(params.row.deductions) ? params.row.deductions : [];
        return formatCurrency(deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0));
      }
    },
    {
      field: "taxableSalary",
      headerName: "Taxable Salary",
      width: 140,
      renderCell: (params) => {
        if (params?.row?.basicSalary == null) return "";
        if (params.row.taxableSalary) return formatCurrency(Math.round(params.row.taxableSalary));
        const basicSalary = Number(params.row.basicSalary) || 0;
        const allowances = Array.isArray(params.row.allowances) ? params.row.allowances : [];
        const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
        return formatCurrency(Math.round((basicSalary + totalAllowances) * 0.85));
      }
    },
    {
      field: "tds",
      headerName: "TDS",
      width: 100,
      renderCell: (params) => formatCurrency(params?.row?.tds ?? 0)
    },
    {
      field: "netSalary",
      headerName: "Net Pay",
      width: 150,
      renderCell: (params) => {
        if (params?.row?.basicSalary == null) return "";
        const basicSalary = Number(params.row.basicSalary) || 0;
        const allowances = Array.isArray(params.row.allowances) ? params.row.allowances : [];
        const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
        const deductions = Array.isArray(params.row.deductions) ? params.row.deductions : [];
        const totalDeductions = deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
        return formatCurrency(basicSalary + totalAllowances - totalDeductions);
      }
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Select
          value={params?.row?.status || ""}
          onChange={(e) => handleStatusChange(params?.row?._id, e.target.value)}
          sx={{ height: 35, width: 120 }}
        >
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Confirmed">Confirmed</MenuItem>
          <MenuItem value="Paid">Paid</MenuItem>
        </Select>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div className="flex gap-2 mt-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/payslip/${params.row._id}`);
            }}
          >
            <FaEdit className="text-blue-500 hover:text-blue-700 text-xl" />
          </button>
          <button onClick={(e) => handleSingleDeleteClick(params.row._id, e)}>
            <FaTrashAlt className="text-red-500 hover:text-red-700 text-xl" />
          </button>
        </div>
      )
    }
  ];

  // -----------------------------
  // Filtered Payslips for DataGrid
  // -----------------------------
  const getFilteredPayslips = () => {
    return payslips.filter((payslip) => {
      const matchesSearch = payslip.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDate = !filterDate ||
        new Date(payslip.endDate).toISOString().slice(0, 7) === filterDate;
      return matchesSearch && matchesDate;
    });
  };

  return (
    <div className="p-4 bg-white">
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <div className="flex gap-4">
          <TextField
            label="Search Employees"
            variant="outlined"
            size="small"
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 300 }}
          />
          <TextField
            label="Filter by Month"
            type="month"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: 200 }}
          />
        </div>
        <div className="flex gap-2 items-center">
          {/* Clicking this button opens the email dialog */}
          <Button
            variant="outlined"
            onClick={handleOpenEmailDialog}
            sx={{ minWidth: 40, padding: "6px" }}
          >
            <EmailIcon />
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            sx={{ minWidth: 40, padding: "6px" }}
          >
            <GetAppIcon />
          </Button>
          {/* Download Menu */}
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
          >
            <MenuItem onClick={() => { downloadPDF(); setDownloadMenuAnchor(null); }}>
              Download PDF
            </MenuItem>
            <MenuItem onClick={() => { downloadCSV(); setDownloadMenuAnchor(null); }}>
              Download CSV
            </MenuItem>
          </Menu>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Actions</InputLabel>
            <Select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              label="Actions"
            >
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={handleOpenBulkStatusDialog}
            sx={{ minWidth: 40, padding: "6px", fontWeight: 600, bgcolor: "#e3fcec", color: "#2e7d32", border: "1px solid #b2dfdb" }}
          >
            Bulk Status
          </Button>
          {/* New delete button for bulk delete */}
          <Button
            variant="outlined"
            color="error"
            onClick={handleBulkDeleteClick}
            disabled={selectedRows.length === 0}
            sx={{ minWidth: 40, padding: "6px" }}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        </div>
      </Box>

      {isLoading ? (
        <div className="flex justify-center items-center h-96">
          <SlidingCubeLoader loading={true} size={50} color="rgba(74, 144, 226, 1)" />
        </div>
      ) : (
        <Paper sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            autoHeight
            rows={getFilteredPayslips()
              .filter((row) => row != null)
              .map(row => ({
                ...row,
                _id: String(row._id || row.id) // Force _id to string
              }))
            }
            columns={columns}
            pageSize={10}
            getRowId={(row) => String(row._id)} // Always string
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection.map(String));
            }}
            rowSelectionModel={selectedRows}
            sx={{
              minWidth: 900,
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#f5f5f5" }
            }}
          />
        </Paper>
      )}

      {/* Dialog for Date Range / Generation */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Set Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2, minHeight: 200 }}>
            <TextField
              label="Select Month"
              type="month"
              value={dates.startDate.slice(0, 7)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
            />
            {/* End date is auto-calculated and disabled */}
            <TextField
              label="Date Range"
              value={`${dates.startDate} to ${dates.endDate}`}
              InputLabelProps={{ shrink: true }}
              disabled
            />
            {selectedAction === "Generate (Single)" && (
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  label="Employee"
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleGenerate} variant="contained">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog: Lists email IDs (instead of names) */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send Payslips via Email</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              label="Select Month"
              type="month"
              value={emailDateFilter}
              onChange={(e) => setEmailDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ bgcolor: "#fff", borderRadius: 2 }}
            />
          </Box>
          <List>
            {getFilteredPayslips()
              .filter(p => !emailDateFilter ||
                new Date(p.endDate).toISOString().slice(0, 7) === emailDateFilter)
              .map((payslip) => (
                <ListItem
                  key={payslip._id}
                  button
                  onClick={() => togglePayslipSelection(payslip._id)}
                >
                  <Checkbox checked={selectedPayslipIds.includes(payslip._id)} />
                  <ListItemText
                    primary={payslip.name}
                    secondary={
                      <>
                        {payslip.email || "No Email"}
                        <br />
                        {new Date(payslip.endDate).toLocaleDateString()}
                      </>
                    }
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendEmails} variant="contained">
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8,
            bgcolor: "#f5f7fa"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, bgcolor: "#e3fcec", color: "#2e7d32", borderBottom: "1px solid #b2dfdb" }}>
          Bulk Status Update
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Select Month"
              type="month"
              value={bulkMonth}
              onChange={(e) => setBulkMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ bgcolor: "#fff", borderRadius: 2 }}
            />
            <Box sx={{ mb: 1, fontWeight: 500, color: "#555" }}>
              Selected Payslips: <span style={{ color: "#2e7d32", fontWeight: 700 }}>{bulkSelectedIds.length}</span>
            </Box>
            <List sx={{ maxHeight: 250, overflowY: "auto", bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
              {getFilteredPayslips().map((payslip, idx) => (
                <React.Fragment key={payslip._id}>
                  <ListItemButton
                    selected={bulkSelectedIds.includes(payslip._id)}
                    onClick={() => toggleBulkPayslipSelection(payslip._id)}
                    sx={{
                      borderRadius: 2,
                      bgcolor: bulkSelectedIds.includes(payslip._id) ? "#e3fcec" : "inherit",
                      transition: "background 0.2s"
                    }}
                  >
                    <Checkbox
                      checked={bulkSelectedIds.includes(payslip._id)}
                      sx={{ color: "#2e7d32" }}
                    />
                    <Avatar sx={{ bgcolor: getRandomColor(payslip._id), mr: 2 }}>
                      {getInitials(payslip.name)}
                    </Avatar>
                    <ListItemText
                      primary={<span style={{ fontWeight: 500 }}>{payslip.name}</span>}
                      secondary={<span style={{ color: "#888" }}>{payslip.email}</span>}
                    />
                  </ListItemButton>
                  {idx < getFilteredPayslips().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                label="Status"
                sx={{ bgcolor: "#fff", borderRadius: 2 }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#f5f7fa", borderTop: "1px solid #b2dfdb" }}>
          <Button onClick={() => setBulkStatusDialogOpen(false)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkStatusUpdate}
            variant="contained"
            color="success"
            sx={{
              fontWeight: 700,
              bgcolor: "#2e7d32",
              color: "#fff",
              boxShadow: 2,
              "&:hover": { bgcolor: "#1b5e20" }
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: "#fee2e2",
          color: "#dc2626",
          fontWeight: 600
        }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            {deleteMode === "single"
              ? "Are you sure you want to delete this payslip?"
              : `Are you sure you want to delete ${deleteTargetIds.length} payslips?`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ fontWeight: 600 }}
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        severity="success"
        onClose={() => setShowToast(false)}
        message={toastMessage}
        open={showToast}
      />
    </div>
  );
};

export default PayslipTable;
